import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface Prop {
  isLoading: boolean;
  templates: { id: number; body: string }[];
  callBack: (id: string) => void;
}

export default function ContactTemplates({
  isLoading,
  templates,
  callBack,
}: Prop) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { t } = useTranslation();

  return (
    <Collapsible
      className="flex flex-col border border-border rounded-md overflow-hidden flex-1 min-h-0"
      defaultOpen={true}
      onOpenChange={setIsOpen}
    >
      <div className="bg-secondary/20 p-2 flex justify-between items-center">
        <span className="font-medium text-sm">{t("ready_text")}</span>
        <CollapsibleTrigger className="cursor-pointer pe-1">
          <AnimatePresence mode="wait" initial={false}>
            {isOpen ? (
              <motion.div
                key="close-icon"
                initial={{ opacity: 0, rotate: -90, scale: 0.8 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 90, scale: 0.8 }}
                transition={{ duration: 0.1 }}
              >
                <ChevronUp size={17} className="text-muted-foreground" />
              </motion.div>
            ) : (
              <motion.div
                key="chevron-icon"
                initial={{ opacity: 0, rotate: 90, scale: 0.8 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: -90, scale: 0.8 }}
                transition={{ duration: 0.1 }}
              >
                <ChevronDown size={17} className="text-muted-foreground" />
              </motion.div>
            )}
          </AnimatePresence>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent className="overflow-y-auto p-2 flex flex-col gap-3 border-t border-border flex-1 min-h-0 scrollbar-thin scrollbar-thumb-muted-foreground/60 scrollbar-track-transparent">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <div className="h-20 p-2 rounded-md border flex flex-col gap-1.5">
              <Skeleton className="p-1.5" />
              <Skeleton className="p-1.5" />
              <Skeleton className="p-1.5 w-9/12" />
              <Skeleton className="p-1.5 w-3/12" />
            </div>
            <div className="h-20 p-2 rounded-md border flex flex-col gap-1.5">
              <Skeleton className="p-1.5" />
              <Skeleton className="p-1.5" />
              <Skeleton className="p-1.5 w-9/12" />
              <Skeleton className="p-1.5 w-3/12" />
            </div>
            <div className="h-20 p-2 rounded-md border flex flex-col gap-1.5">
              <Skeleton className="p-1.5" />
              <Skeleton className="p-1.5" />
              <Skeleton className="p-1.5 w-9/12" />
              <Skeleton className="p-1.5 w-3/12" />
            </div>
          </div>
        )}
        {!isLoading && templates.length < 1 && (
          <div className="text-center rounded p-1 px-2 text-muted-foreground text-sm">
            {t("no_template_to_send")}
          </div>
        )}
        {!isLoading &&
          templates?.map((template) => (
            <div
              key={template.id}
              className={cn(
                "p-2 rounded-md border cursor-pointer transition hover:shadow",
                selectedId === template.id
                  ? "border-primary bg-primary/10"
                  : "border-input"
              )}
              onClick={() => {
                setSelectedId(template.id);
                callBack(template.id.toString());
              }}
            >
              <div className="text-sm whitespace-pre-line">{template.body}</div>
            </div>
          ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
